/**
 * ビルド時の環境変数から認証と API の接続先を読む。**接続先の知識はここに集約する**。
 *
 * admin 用クライアントの登録の形（client_id の出どころ・scope・resource・
 * テナントの渡し方）は API 側の実装が進むと細部が動くので、画面や認証の
 * 状態機械へ散らさずこの 1 モジュールに閉じ込めてある。
 *
 * 既定値は必須の 2 つ（issuer と API の URL）には持たせない。未設定なら
 * 起動時に画面へ出して止める（黙って別の接続先へ行かせない）。
 *
 * 値は `import.meta.env` から来るが、この関数自体は素のレコードを受け取る純関数に
 * してある。テストで環境を作らずに欠落と既定値を確かめられるようにするため。
 */

export type AdminAuthConfig = {
  /** 認可サーバーの issuer。RFC 8414 のメタデータをここから引く */
  issuer: string;
  /** メタデータを引けないときに使う明示の認可エンドポイント */
  authorizationEndpoint: string | null;
  /** 同じく明示のトークンエンドポイント */
  tokenEndpoint: string | null;
  /** 同じく明示の動的クライアント登録エンドポイント（RFC 7591） */
  registrationEndpoint: string | null;
  /**
   * 固定の client_id。認可サーバーが固定値を配る運用に変わったときのための口で、
   * 未設定なら動的クライアント登録（RFC 7591）で取りに行く。
   */
  clientId: string | null;
  /** 動的登録のときに名乗る名前 */
  clientName: string;
  /** 顧客向けは admin、オペレーター向けは admin-operator */
  scope: string;
  /** access token の aud（RFC 8707 の resource indicator）。未設定なら送らない */
  resource: string | null;
  /** 認可コードを受ける自アプリ側のパス。静的サイトの SPA フォールバックで受ける */
  redirectPath: string;
  /** admin GraphQL の POST 先 */
  graphqlEndpoint: string;
  /** 疎通確認用（GET・認証不要）。設定されていれば失敗時の切り分けに使う */
  healthEndpoint: string | null;
  /**
   * テナントを指定して認可し直すときに載せる認可リクエストのパラメータ名。
   * テナントはトークンのクレームから決まるので、切替は認可のやり直しになる。
   */
  tenantParam: string;
  /**
   * リロード時に、トップレベルの prompt=none 認可でセッションを復元するか。
   * HttpOnly セッションを提供する認可サーバーに接続するときだけ有効にする。
   * サーバー未対応の開発環境にも接続できるよう、既定では無効。
   */
  silentAuth: boolean;
};

export type AdminAuthConfigResult =
  { ok: true; config: AdminAuthConfig } | { ok: false; missing: string[] };

export type RawEnv = Record<string, string | boolean | undefined>;

export type AdminAuthConfigOptions = {
  /** アプリごとの既定スコープ。顧客向け admin / オペレーター向け admin-operator */
  defaultScope: string;
  /** 動的クライアント登録で名乗る名前 */
  defaultClientName: string;
};

const REQUIRED = ["VITE_ADMIN_OAUTH_ISSUER", "VITE_ADMIN_API_URL"] as const;

function text(env: RawEnv, key: string): string {
  const value = env[key];
  return typeof value === "string" ? value.trim() : "";
}

function optional(env: RawEnv, key: string): string | null {
  const value = text(env, key);
  return value === "" ? null : value;
}

function flag(env: RawEnv, key: string): boolean {
  const value = env[key];
  return value === true || value === "1" || value === "true";
}

/** 末尾のスラッシュだけ落とす。パス付きの issuer（`https://host/admin/oauth`）を壊さない */
function trimTrailingSlash(value: string): string {
  return value.endsWith("/") ? value.slice(0, -1) : value;
}

export function readAdminAuthConfig(
  env: RawEnv,
  options: AdminAuthConfigOptions,
): AdminAuthConfigResult {
  const missing = REQUIRED.filter((key) => text(env, key) === "");
  if (missing.length > 0) {
    return { ok: false, missing };
  }
  const issuer = trimTrailingSlash(text(env, "VITE_ADMIN_OAUTH_ISSUER"));
  return {
    ok: true,
    config: {
      issuer,
      authorizationEndpoint: optional(
        env,
        "VITE_ADMIN_OAUTH_AUTHORIZATION_ENDPOINT",
      ),
      tokenEndpoint: optional(env, "VITE_ADMIN_OAUTH_TOKEN_ENDPOINT"),
      registrationEndpoint: optional(
        env,
        "VITE_ADMIN_OAUTH_REGISTRATION_ENDPOINT",
      ),
      clientId: optional(env, "VITE_ADMIN_OAUTH_CLIENT_ID"),
      clientName:
        optional(env, "VITE_ADMIN_OAUTH_CLIENT_NAME") ??
        options.defaultClientName,
      scope: optional(env, "VITE_ADMIN_OAUTH_SCOPE") ?? options.defaultScope,
      // 既定は issuer の直下。API 側が配っている aud と同じ形にしてある
      resource:
        optional(env, "VITE_ADMIN_OAUTH_RESOURCE") ?? `${issuer}/graphql`,
      redirectPath:
        optional(env, "VITE_ADMIN_OAUTH_REDIRECT_PATH") ?? "/auth/callback",
      graphqlEndpoint: text(env, "VITE_ADMIN_API_URL"),
      healthEndpoint: optional(env, "VITE_ADMIN_API_HEALTH_URL"),
      tenantParam:
        optional(env, "VITE_ADMIN_OAUTH_TENANT_PARAM") ?? "tenant_id",
      silentAuth: flag(env, "VITE_ADMIN_OAUTH_SILENT_AUTH"),
    },
  };
}

/** モック API で動かすか。開発ビルドでしか効かない（本番では必ず false になる） */
export function readUseMockApi(env: RawEnv, dev: boolean): boolean {
  return dev && flag(env, "VITE_ADMIN_USE_MOCK_API");
}

/**
 * 招待の受け取りの口（`POST /admin/invitations/accept`）。
 *
 * **接続先の知識をここから増やさない。** 受け取りの口は GraphQL と同じ
 * `/admin` の下に居るので、環境変数を 1 つ増やすのではなく、GraphQL の
 * 接続先の末尾を差し替えて作る。増やすと、2 つの環境変数が食い違った
 * まま動く面ができる（片方だけ直して気づけない）。
 *
 * 末尾が `/graphql` でない形を渡されたら `null` を返す — 組み立てられない
 * ものを推測で組み立てない（招待の受け取りができない面では、画面が
 * その旨を出す）。
 */
export function invitationAcceptEndpoint(
  graphqlEndpoint: string,
): string | null {
  const suffix = "/graphql";
  return graphqlEndpoint.endsWith(suffix)
    ? `${graphqlEndpoint.slice(0, -suffix.length)}/invitations/accept`
    : null;
}
